import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Layout from '../Layout/Layout';
import { useIsRestaurant } from '../../hooks/useAccountType';
import { useEntitlements } from '../../hooks/useEntitlements';

import CafeVibesWizard from './wizard/CafeVibesWizard';
import QuickPromoWizard from './wizard/QuickPromoWizard';
import MatchWizard from './wizard/MatchWizard';
import './wizard/fastcreate.css';

/**
 * Café "+ Create" — the template picker that replaces the technical campaign
 * builder for café owners. Pick a template → a short wizard builds it and taps
 * play. The full editor is still reachable via "Advanced editor".
 *
 * ⚠️ FIVE CARDS, NOT EIGHT (2026-09). The three daypart cards (🌅🌞🌆) left
 * the picker entirely — they were the SAME flow as ☕ repeated three times,
 * distinguished only by which Remote-Control button they became, which
 * nothing on this screen explained. That choice is now asked at the END of
 * the ☕/🎬 flows (the new "أين تحفظه؟" save step in CafeVibesWizard), where
 * the owner already knows what they built and can see each slot's own
 * occupancy before choosing. Ported from .claude/fastcreate-prototype.html —
 * markup/CSS/copy verbatim, not rebuilt.
 *
 * CAFÉ ONLY: restaurant accounts are redirected to their Video Wall (defensive;
 * CompanyDetailsRouter already blocks non-video-wall routes for them). This page
 * is net-new and never touches the restaurant flow.
 */
const TEMPLATES = [
  { k: 'vibes', ic: '☕', need: true },
  { k: 'silent', ic: '🎬' },
  { k: 'match', ic: '🏟️' },
  { k: 'match_music', ic: '🏟️🎵' },
  { k: 'promo', ic: '📢' },
];

export default function CafeCreate() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isRestaurant = useIsRestaurant();
  // The two match templates drive receivers/channels (hardware_control): hidden when the package explicitly lacks it.
  const hwOn = useEntitlements().has('hardware_control');
  const [picked, setPicked] = useState(null);

  if (isRestaurant) return <Navigate to="/apps/video-wall" replace />;

  const exit = () => setPicked(null);
  const done = () => navigate('/devices/list');

  const renderWizard = () => {
    switch (picked) {
      case 'vibes':       return <CafeVibesWizard mode="vibes"  onExit={exit} onDone={done} />;
      case 'silent':      return <CafeVibesWizard mode="silent" onExit={exit} onDone={done} />;
      case 'promo':       return <QuickPromoWizard onExit={exit} onDone={done} />;
      case 'match':       return <MatchWizard mode="commentary" onExit={exit} onDone={done} />;
      case 'match_music': return <MatchWizard mode="music"      onExit={exit} onDone={done} />;
      default:            return null;
    }
  };

  return (
    <Layout>
      <div className="fc-wizard">
        {picked ? (
          renderWizard()
        ) : (
          <div className="fc-shell">
            <div className="fc-h1">{t('cafe_wizard.picker_title')}</div>
            <div className="fc-h2">{t('cafe_wizard.picker_subtitle')}</div>

            <div className="fc-grid">
              {TEMPLATES.filter((tpl) => hwOn || !tpl.k.startsWith('match')).map((tpl) => (
                <button
                  key={tpl.k} type="button" className="fc-opt"
                  onClick={() => setPicked(tpl.k)}
                >
                  <div className="ic">{tpl.ic}</div>
                  <div className="t">{t(`cafe_wizard.tpl_${tpl.k}_title`)}</div>
                  <div className="s">{t(`cafe_wizard.tpl_${tpl.k}_desc`)}</div>
                  {tpl.need && <div className="need">{t('cafe_wizard.most_used')}</div>}
                </button>
              ))}
            </div>

            <div className="fc-adv">
              {t('cafe_wizard.advanced_editor_prompt')}{' '}
              <a href="/campaigns/list" onClick={(e) => { e.preventDefault(); navigate('/campaigns/list'); }}>
                {t('cafe_wizard.advanced_editor')}
              </a>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
